import { Body, Square } from "./Body";
import { GameManager } from "./GameManager";
import { Speech } from "./TalkPanel";
import { Player, CheckPoint, Goal, Signboard } from "./UtilBody";
import { Vector } from "./Vector";

export abstract class Stage {
    private bodies: Body[] = [];
    private gameManager: GameManager = new GameManager();
    constructor() {
        this.gameManager.addEvent(["gameLoop"], () => {
            this.loop();
        });
    }

    get g$element(): HTMLDivElement {
        return this.gameManager.element;
    }

    get g$gameManager(): GameManager {
        return this.gameManager;
    }

    protected pushSquare(x: number, y: number, width: number, height: number) {
        const body = new Square();
        body.setPosition(new Vector(x, y));
        body.setSize(new Vector(width, height));
        this.bodies.push(body);
        this.s$zIndex = 2;
    }

    protected pushWall(x: number, y: number, width: number, height: number) {
        this.pushSquare(x, y, width, height);
        this.asWall();
    }

    protected pushPlayer(x: number, y: number) {
        const body = new Player();
        body.setPosition(new Vector(x, y));
        this.bodies.push(body);
        this.gameManager.s$player = body;
        this.s$zIndex = 5;
    }

    protected pushCheckPoint(x: number, y: number) {
        const body = new CheckPoint();
        body.setPosition(new Vector(x, y));
        this.bodies.push(body);
        this.s$zIndex = 2;
    }

    protected pushGoal(x: number, y: number, width: number, height: number) {
        const body = new Goal();
        body.setPosition(new Vector(x, y));
        body.setSize(new Vector(width, height));
        this.bodies.push(body);
        this.s$zIndex = 2;
    }

    protected pushDescription(x: number, y: number, width: number, height: number, description: string) {
        this.pushSquare(x, y, width, height);
        this.get().element.innerHTML = description;
        this.get().element.classList.add("description");
        this.s$movable = false;
        this.s$touchable = false;
    }

    protected pushSignboard(x: number, y: number, talk: Speech[]) {
        const body = new Signboard(talk);
        body.setPosition(new Vector(x, y));
        this.bodies.push(body);
        this.get().element.classList.add("signboard");
        this.s$movable = false;
        this.s$touchable = false;
        this.s$zIndex = 1;
    }

    protected pushFakePanel(x: number, y: number, width: number, height: number) {
        this.pushSquare(x, y, width, height);
        this.get().element.classList.add("fakePanel");
        this.get().element.style.zIndex = "6";
        this.s$movable = false;
        this.s$touchable = false;
    }

    protected set s$movable(movable: boolean) {
        this.get().movable = movable;
    }
    protected set s$ableToBeMoved(ableToBeMoved: boolean) {
        this.get().ableToBeMoved = ableToBeMoved;
    }
    protected set s$touchable(touchable: boolean) {
        this.get().touchable = touchable;
    }

    protected set s$visible(visible: boolean) {
        if (visible) {
            this.get().element.style.opacity = "1";
        } else {
            this.get().element.style.opacity = "0";
        }
    }

    protected set s$id(id: string) {
        this.get().element.id = id;
    }

    protected set s$className(className: string) {
        this.get().element.classList.add(className);
    }

    protected set s$color(color: string) {
        this.get().element.style.backgroundColor = color;
    }

    protected set s$zIndex(zIndex: number) {
        this.get().element.style.zIndex = zIndex + "";
    }

    protected set s$fontSize(fontSize: number) {
        this.get().element.style.fontSize = fontSize * 10 + "px";
    }

    protected asWall() {
        this.s$movable = false;
        this.s$ableToBeMoved = false;
        this.get().element.classList.add("wall");
    }

    protected asCameraCenter() {
        this.gameManager.s$cameraCenter = this.get();
    }

    protected get() {
        return this.bodies[this.bodies.length - 1];
    }

    protected respawnCheckPoint(index: number) {
        const checkPoint = this.bodies.filter((body) => body instanceof CheckPoint)[index];
        checkPoint.s$booted = true;
        this.gameManager.checkPoint = checkPoint;
        const players = this.bodies.filter((body) => body instanceof Player);
        players[players.length - 1].die();
    }

    protected abstract loop(): void;

    protected create() {
        this.gameManager.addBodies(this.bodies);
    }
}
